/**
 * Generic input sanitizers shared across forms.
 *
 * - stripDigits: for PERSON-name fields only. Do NOT use it on a company or
 *   organisation name: digits are legitimate there (3M, 24/7 Services, ABC 123
 *   Engineering), and stripping them deletes the user's input as they type with
 *   no message saying why.
 * - sanitizeMobile: for mobile/phone fields — keeps digits only, capped at
 *   `maxLen` (default 10; pass mobileMaxDigits(country) for other countries).
 */

/** Remove all digits — PERSON names only. See the note above before reusing. */
export const stripDigits = (v) => (v || '').replace(/[0-9]/g, '');

/** Keep digits only, limited to `maxLen` (default 10) — for mobile numbers. */
export const sanitizeMobile = (v, maxLen = 10) =>
  (v || '').replace(/[^0-9]/g, '').slice(0, maxLen);

/** Digits required in a contact number. */
export const MOBILE_DIGITS = 10;

/**
 * Is this a complete contact number?
 *
 * `sanitizeMobile` stops a field growing past ten digits, but it says nothing
 * about a SHORT one — so a form that only checked the field was non-empty
 * accepted "9" as an emergency contact number. Use this wherever a mobile is
 * required, and validate on the server too: an HTML `pattern` fires only on
 * native form submission, and these forms submit through JavaScript.
 */
export const isValidMobile = (v) =>
  new RegExp(`^[0-9]{${MOBILE_DIGITS}}$`).test(String(v ?? '').trim());

/*
 * REG-13: country-aware mobile rules, shared by components/PhoneInput.jsx and
 * any field that only needs the digit cap (e.g. contractor phone).
 *
 * `minDigits`/`maxDigits` are NATIONAL digits (no country code). Malaysia
 * allows 9–11 so both "123456789" and the trunk-prefixed "0123456789" /
 * "01123456789" forms are accepted. The legacy fixed 10-digit helpers above
 * (MOBILE_DIGITS / isValidMobile) are unchanged — the PTW emergency contact
 * number is still enforced as exactly 10 digits by the backend.
 */
export const MOBILE_COUNTRY_OPTIONS = [
  { code: 'OTHER', label: 'Other', dialCode: '', minDigits: 7, maxDigits: 20, hint: '7–20 digits' },
  { code: 'IN', label: 'India (+91)', dialCode: '91', minDigits: 10, maxDigits: 10, hint: '10 digits' },
  { code: 'SG', label: 'Singapore (+65)', dialCode: '65', minDigits: 8, maxDigits: 8, hint: '8 digits' },
  { code: 'MY', label: 'Malaysia (+60)', dialCode: '60', minDigits: 9, maxDigits: 11, hint: '9–11 digits' },
  { code: 'EU', label: 'Europe', dialCode: '', minDigits: 7, maxDigits: 15, hint: '7–15 digits' },
];

const MOBILE_ERRORS = {
  IN: 'India mobile numbers must be exactly 10 digits',
  SG: 'Singapore mobile numbers must be exactly 8 digits',
  MY: 'Malaysia mobile numbers must be 9–11 digits',
  EU: 'European mobile numbers must be 7–15 digits',
  OTHER: 'Please enter a valid mobile number',
};

/** Rules for a phone-country code; unknown codes use 'OTHER'. */
export const mobileRules = (country) =>
  MOBILE_COUNTRY_OPTIONS.find((o) => o.code === String(country || '').toUpperCase())
  || MOBILE_COUNTRY_OPTIONS[0];

/** Max national digits for a country (IN → 10, MY → 11, unknown → 20). */
export const mobileMaxDigits = (country) => mobileRules(country).maxDigits;

/**
 * Country-aware validation. Returns null when valid or empty, else a message.
 * Separators (space, dash, brackets, dot, plus) are ignored.
 */
export const validateMobile = (mobile, country) => {
  if (!mobile) return null;
  const digits = String(mobile).replace(/[\s\-().+]/g, '');
  const r = mobileRules(country);
  const ok = new RegExp(`^\\d{${r.minDigits},${r.maxDigits}}$`).test(digits);
  return ok ? null : (MOBILE_ERRORS[r.code] || MOBILE_ERRORS.OTHER);
};

/**
 * Reduce a stored/free-form phone ("+60 12-345 6789", "+91 98765 43210",
 * "098765 43210") to national digits for `country`: a leading country dial
 * code is dropped when what remains is a valid length (India also drops the
 * trunk "0"), then capped at the country max. Replaces the old
 * `.slice(-10)` autofill, which silently kept the LAST ten digits.
 */
export const toNationalDigits = (value, country) => {
  const r = mobileRules(country);
  const raw = String(value || '').trim();
  let digits = raw.replace(/[^0-9]/g, '');
  const fits = (d) => d.length >= r.minDigits && d.length <= r.maxDigits;
  if (r.dialCode) {
    // Explicit international form ("+60…" / "0060…") — always the dial code.
    const explicitIntl = raw.startsWith(`+${r.dialCode}`) || digits.startsWith(`00${r.dialCode}`);
    if (digits.startsWith(`00${r.dialCode}`)) digits = digits.slice(2);
    if (digits.startsWith(r.dialCode)
        && (explicitIntl || !fits(digits))
        && fits(digits.slice(r.dialCode.length))) {
      digits = digits.slice(r.dialCode.length);
    }
  }
  if (r.code === 'IN' && digits.length === 11 && digits.startsWith('0')) {
    digits = digits.slice(1);
  }
  return digits.slice(0, r.maxDigits);
};

/*
 * Emergency contact numbers (PTW). The backend stores Indian numbers as ten
 * national digits and every other country as "+<E.164 digits>"
 * (backend/utils/contact_validation.py). India and tenants with no country keep
 * the legacy exact-10-digit rule; other countries validate per mobileRules and
 * are submitted in international form so the server can accept them.
 */
const usesIndianContactRule = (country) => {
  const c = String(country || '').toUpperCase();
  return !c || c === 'IN' || !mobileRules(c).dialCode;
};

export const isValidContactNumber = (value, country) => (
  usesIndianContactRule(country)
    ? isValidMobile(value)
    : !!value && validateMobile(value, country) === null
);

export const toContactSubmitValue = (value, country) => {
  if (usesIndianContactRule(country) || !value) return value;
  const national = String(value).replace(/[^0-9]/g, '').replace(/^0+/, '');
  return `+${mobileRules(country).dialCode}${national}`;
};
